/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/jsx-props-no-spreading */
import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Center from '../../assets/styles/Center';
import FormContainer from '../../assets/styles/FormContainer';
import Text from '../../assets/styles/Text';
import FormAuth from '../../components/FormAuth';
import GroupFile from '../../components/GroupFile';
import GroupInput from '../../components/GroupInput';
import Title from '../../components/Title';
import {
  useUpdateClient,
  useInitiateVerification,
  useVerifyDocument,
} from '../../hooks/mutations/useClientMutations';
import uploadS3 from '../../services/utils/uploadS3';
import { useStore } from '../../stores';
import * as styles from './styles';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

export default function ClientUploads() {
  const navigate = useNavigate();
  const { id: userId } = useStore((state) => state.user);

  const [phone, setPhone] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const updateClient = useUpdateClient();
  const initiateVerification = useInitiateVerification();
  const verifyDocument = useVerifyDocument();

  // Cleanup preview on unmount
  useEffect(
    () => () => {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
    },
    [preview]
  );

  // Handle file drop
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];

    // Validate file type
    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast.error('Apenas imagens JPEG e PNG são aceitas');
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      toast.error('Arquivo muito grande. Máximo: 10MB');
      return;
    }

    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    maxFiles: 1,
    multiple: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!phone || phone.length < 10) {
      toast.error('Digite um número de telefone válido');
      return;
    }

    if (!selectedFile) {
      toast.error('Selecione um documento');
      return;
    }

    try {
      // Update client with phone number
      await updateClient.mutateAsync({
        id: userId,
        phone,
      });

      // Initiate verification to get presigned URL
      const result = await initiateVerification.mutateAsync({
        filename: selectedFile.name,
        size: selectedFile.size,
        type: selectedFile.type,
      });

      // Upload file to S3
      const { url, key } = result.data.documentUpload;
      await uploadS3(url, selectedFile);

      // Confirm document upload
      await verifyDocument.mutateAsync(key);

      toast.success('Documento enviado com sucesso!');
      navigate('/clientdashboard');
    } catch (error) {
      console.error('Erro ao enviar documento:', error);
    }
  };

  const isLoading =
    updateClient.isPending ||
    initiateVerification.isPending ||
    verifyDocument.isPending;

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    }
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setPhone(formatted);
  };

  const isFormValid = phone.length >= 14 && !!selectedFile;

  return (
    <FormContainer>
      <Center className="center">
        <Title
          title="Envio de documento"
          subtitle="Adicione seu telefone e envie seu documento"
        />

        <FormAuth
          onSubmit={handleSubmit}
          buttonTitle="Enviar documento"
          isLoading={isLoading}
          formValid={isFormValid}
        >
          <GroupFile
            title="Foto do documento"
            idForm="document_photo"
            type={3}
            prev={preview ? { document_photo: preview } : null}
          >
            <div {...getRootProps()}>
              <input {...getInputProps()} id="document_photo" />
            </div>
          </GroupFile>

          {selectedFile && (
            <styles.FileInfo>
              <Text.Span>Arquivo selecionado: {selectedFile.name}</Text.Span>
              <Text.Span>
                Tamanho: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </Text.Span>
            </styles.FileInfo>
          )}

          <GroupInput>
            <label>Número de telefone*</label>
            <input
              type="text"
              placeholder="(00) 00000-0000"
              value={phone}
              onChange={handlePhoneChange}
              maxLength={15}
            />
          </GroupInput>
        </FormAuth>
      </Center>
    </FormContainer>
  );
}
